import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';

const NO_SWIPE_SELECTOR =
  'model-viewer, video, iframe, .deferred-media__poster-button, .product-media__drag-zoom-wrapper, .product-media-container__zoom-button, product-model';

const STORAGE_PREFIX = 'tinypresent:dynamic-slides:';
const SLIDE_TRANSITION_MS = 480;
/** Visible thumbs counted as the strip edge; clicking these peeks more. */
const THUMB_EDGE_COUNT = 2;
/** How many extra thumbs to reveal when an edge thumb is clicked. */
const THUMB_PEEK_SHIFT = 2;
const DESIGN_BADGE_ICON = '✨';
const DESIGN_BADGE_LABEL = 'Your Design';
const DESIGN_BADGE_MAIN_HTML = `<span class="product-media-design-badge" aria-hidden="true"><span class="product-media-design-badge__icon">${DESIGN_BADGE_ICON}</span><span class="product-media-design-badge__label">${DESIGN_BADGE_LABEL}</span></span>`;
const DESIGN_BADGE_THUMBNAIL_HTML = `<span class="slideshow-controls__thumbnail-badge product-media-design-badge--thumbnail" aria-hidden="true">${DESIGN_BADGE_ICON}</span>`;

/**
 * @param {string} url
 * @param {{ lazy?: boolean }} [options]
 */
function buildThumbMfImg(url, options = {}) {
  const lazyAttr = options.lazy === false ? '' : ' lazy';
  return `<mf-img class="product-media-thumb"${lazyAttr} alt="" style="--mf-img-object-fit: cover; --mf-img-object-position: center; --mf-img-height: 100%; --mf-img-bg: #fafafa;"><source src="${url}"></mf-img>`;
}

/**
 * @typedef {object} DynamicSlideData
 * @property {string} id
 * @property {string} url
 * @property {'ai' | 'history'} source
 * @property {number} [createdAt]
 */

/**
 * Swiper-powered product media carousel with thumbs support.
 * Exposes a slideshow-compatible API for media-gallery.js and ai-product-media.js.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement} mainSwiperEl
 * @property {HTMLElement} slidesContainer
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} [thumbsSwiperEl]
 * @property {HTMLElement} [thumbsSlidesContainer]
 * @property {HTMLElement} [mobileThumbsSwiperEl]
 * @property {HTMLElement} [mobileThumbsSlidesContainer]
 * @property {HTMLElement} [paginationEl]
 * @property {HTMLElement} [mobilePaginationEl]
 *
 * @extends {Component<Refs>}
 */
export class ProductMediaSwiper extends Component {
  requiredRefs = ['mainSwiperEl', 'slidesContainer'];

  /** @type {import('swiper').Swiper | null} */
  #mainSwiper = null;

  /** @type {import('swiper').Swiper | null} */
  #thumbsSwiper = null;

  /** @type {import('swiper').Swiper | null} */
  #mobileThumbsSwiper = null;

  /** @type {number} */
  #current = 0;

  /** @type {DynamicSlideData[]} */
  #dynamicSlides = [];

  /** @type {number | null} */
  #currentVariantId = null;

  /** @type {number} */
  #loadVisibleFrame = 0;

  /** @type {number} */
  #loadVisibleTimer = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initWhenReady();
  }

  disconnectedCallback() {
    this.#destroySwipers();
    super.disconnectedCallback();
  }

  async #initWhenReady() {
    await this.#waitForSwiper();
    if (!this.isConnected) return;

    requestAnimationFrame(async () => {
      if (!this.isConnected) return;
      this.#currentVariantId = this.#parseIntAttr('initialVariantId');
      this.#initSwipers();
      this.#applyInitialVariantVisibility();
      await this.#restoreDynamicSlides();
    });
  }

  #waitForSwiper() {
    return new Promise((resolve) => {
      if (window.Swiper) {
        resolve();
        return;
      }

      const check = () => {
        if (window.Swiper) {
          resolve();
          return;
        }
        requestAnimationFrame(check);
      };

      check();
    });
  }

  #destroySwipers() {
    if (this.#mainSwiper?.params?.thumbs) {
      this.#mainSwiper.params.thumbs.swiper = null;
    }

    this.#mainSwiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#mobileThumbsSwiper?.destroy(true, true);
    this.#mainSwiper = null;
    this.#thumbsSwiper = null;
    this.#mobileThumbsSwiper = null;
  }

  #getThumbDirection() {
    return this.dataset.thumbDirection === 'vertical' ? 'vertical' : 'horizontal';
  }

  #initSwipers() {
    const Swiper = window.Swiper;
    if (!Swiper || this.#mainSwiper) return;

    const thumbsEl = this.refs.thumbsSwiperEl;
    const mobileThumbsEl = this.refs.mobileThumbsSwiperEl;
    const thumbDirection = this.#getThumbDirection();
    const thumbConfig = {
      slidesPerView: 'auto',
      spaceBetween: 8,
      freeMode: true,
      watchSlidesProgress: true,
      slideToClickedSlide: false,
      direction: thumbDirection,
      observer: true,
      observeParents: true,
      on: {
        slideChange: () => this.#scheduleLoadVisibleThumbs(),
      },
    };

    if (thumbsEl instanceof HTMLElement) {
      this.#thumbsSwiper = new Swiper(thumbsEl, {
        ...thumbConfig,
        slidesPerView: thumbDirection === 'vertical' ? 'auto' : 8,
      });
    }

    if (mobileThumbsEl instanceof HTMLElement) {
      this.#mobileThumbsSwiper = new Swiper(mobileThumbsEl, {
        ...thumbConfig,
        direction: 'horizontal',
      });
    }

    const linkedThumbs = this.#thumbsSwiper || this.#mobileThumbsSwiper;
    const spaceBetween = parseInt(this.dataset.spaceBetween || '0', 10) || 0;
    const speed = this.#getTransitionSpeed();
    const slideCount = this.slides.length;
    const nextEl = this.querySelector('.swiper-button-next');
    const prevEl = this.querySelector('.swiper-button-prev');
    const pagination = this.#buildPaginationConfig();

    /** @type {import('swiper').SwiperOptions} */
    const mainConfig = {
      effect: 'slide',
      slidesPerView: 1,
      slidesPerGroup: 1,
      speed,
      spaceBetween,
      initialSlide: this.#getInitialSlideIndex(),
      grabCursor: slideCount > 1,
      resistanceRatio: 0.85,
      longSwipesRatio: 0.25,
      threshold: 8,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      noSwipingSelector: NO_SWIPE_SELECTOR,
      on: {
        slideChange: () => {
          this.#onSlideChange();
          this.#scheduleLoadVisibleThumbs();
        },
      },
    };

    // Swiper extend() overwrites module defaults with explicit undefined — omit unused modules.
    if (nextEl instanceof HTMLElement && prevEl instanceof HTMLElement) {
      mainConfig.navigation = { nextEl, prevEl };
    }

    if (pagination) {
      mainConfig.pagination = pagination;
    }

    if (linkedThumbs) {
      mainConfig.thumbs = {
        swiper: linkedThumbs,
        multipleActiveThumbs: false,
      };
    }

    this.#mainSwiper = new Swiper(this.refs.mainSwiperEl, mainConfig);

    this.#bindThumbClicks(this.#thumbsSwiper);
    this.#bindThumbClicks(this.#mobileThumbsSwiper);
    this.#syncMultiSlideMode(slideCount);
    this.#current = this.#mainSwiper.activeIndex;
    this.#syncAriaHidden();
    this.#pauseInactiveMedia();
  }

  /**
   * mf-img uses Shadow DOM; Swiper's click target may be the inner img and
   * `closest('.swiper-slide')` then fails. Resolve the slide via composedPath.
   * @param {import('swiper').Swiper | null} thumbsSwiper
   */
  #bindThumbClicks(thumbsSwiper) {
    const thumbsEl = thumbsSwiper?.el;
    if (!(thumbsEl instanceof HTMLElement)) return;

    thumbsEl.addEventListener('click', (event) => {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
      const slide = path.find(
        (node) =>
          node instanceof HTMLElement &&
          node.classList.contains('swiper-slide') &&
          thumbsEl.contains(node)
      );
      if (!(slide instanceof HTMLElement)) return;

      const index = thumbsSwiper.slides.indexOf(slide);
      if (index < 0) return;

      const peekTarget = this.#thumbPeekTarget(thumbsSwiper, index);

      if (index !== this.#mainSwiper?.activeIndex) {
        this.slideToIndex(index);
      }

      this.#slideThumbsToPeek(thumbsSwiper, peekTarget);
    });
  }

  /**
   * If the clicked thumb sits in the first/last visible pair, shift the strip
   * by two so the next hidden thumbs become clickable without dragging.
   * @param {import('swiper').Swiper} thumbsSwiper
   * @param {number} clickedIndex
   * @returns {number | null}
   */
  #thumbPeekTarget(thumbsSwiper, clickedIndex) {
    const { first, last } = this.#visibleThumbRange(thumbsSwiper);
    const total = thumbsSwiper.slides.length;
    if (total <= 1 || first < 0 || last < first) return null;

    if (clickedIndex >= last - THUMB_EDGE_COUNT + 1 && clickedIndex <= last) {
      return Math.min(first + THUMB_PEEK_SHIFT, total - 1);
    }

    if (clickedIndex >= first && clickedIndex <= first + THUMB_EDGE_COUNT - 1) {
      return Math.max(first - THUMB_PEEK_SHIFT, 0);
    }

    return null;
  }

  /**
   * @param {import('swiper').Swiper} thumbsSwiper
   * @returns {{ first: number, last: number }}
   */
  #visibleThumbRange(thumbsSwiper) {
    const slides = Array.from(thumbsSwiper.slides || []);
    const wrap = thumbsSwiper.el?.getBoundingClientRect();
    if (!wrap || !slides.length) return { first: -1, last: -1 };

    const vertical = thumbsSwiper.params?.direction === 'vertical';
    let first = -1;
    let last = -1;

    slides.forEach((slide, index) => {
      if (!(slide instanceof HTMLElement)) return;
      const box = slide.getBoundingClientRect();
      const start = vertical ? box.top : box.left;
      const end = vertical ? box.bottom : box.right;
      const wrapStart = vertical ? wrap.top : wrap.left;
      const wrapEnd = vertical ? wrap.bottom : wrap.right;
      const size = end - start;
      const visible = Math.min(end, wrapEnd) - Math.max(start, wrapStart);
      if (size <= 0 || visible / size < 0.5) return;
      if (first < 0) first = index;
      last = index;
    });

    if (first >= 0) return { first, last };

    const indexes = thumbsSwiper.visibleSlidesIndexes;
    if (Array.isArray(indexes) && indexes.length) {
      return { first: indexes[0], last: indexes[indexes.length - 1] };
    }

    return { first: 0, last: slides.length - 1 };
  }

  /**
   * Apply after the main slide change so Swiper thumbs sync does not overwrite.
   * @param {import('swiper').Swiper} thumbsSwiper
   * @param {number | null} peekTarget
   */
  #slideThumbsToPeek(thumbsSwiper, peekTarget) {
    if (peekTarget == null) return;

    const speed = this.#getTransitionSpeed();
    const apply = () => {
      thumbsSwiper.slideTo(peekTarget, speed);
      const other =
        thumbsSwiper === this.#thumbsSwiper ? this.#mobileThumbsSwiper : this.#thumbsSwiper;
      if (other && other !== thumbsSwiper) {
        other.slideTo(peekTarget, speed);
      }
    };

    requestAnimationFrame(() => requestAnimationFrame(apply));
  }

  #buildPaginationConfig() {
    const paginationEl = this.refs.paginationEl || this.refs.mobilePaginationEl;
    if (!(paginationEl instanceof HTMLElement)) return undefined;

    return {
      el: paginationEl,
      clickable: true,
    };
  }

  #onSlideChange() {
    const previousIndex = this.#current;
    const index = this.#mainSwiper?.activeIndex ?? 0;

    if (index === previousIndex) return;

    this.#current = index;
    this.#syncAriaHidden();
    this.#pauseInactiveMedia();

    const slide = this.slides[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  #syncAriaHidden() {
    const activeIndex = this.#mainSwiper?.activeIndex ?? 0;

    this.slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== activeIndex}`);
    });
  }

  #pauseInactiveMedia() {
    const activeIndex = this.#mainSwiper?.activeIndex ?? 0;

    this.slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.querySelectorAll('video').forEach((video) => {
        if (isActive) return;
        if (!video.paused) video.pause();
      });
    });
  }

  #syncMultiSlideMode(slideCount) {
    const isMulti = slideCount > 1;

    if (isMulti) {
      this.classList.add('slideshow--has-multiple-slides');
      this.classList.remove('slideshow--single-media', 'product-media-gallery__slideshow--single-media');
      this.removeAttribute('disabled');
      return;
    }

    this.classList.remove('slideshow--has-multiple-slides');
    this.classList.add('slideshow--single-media', 'product-media-gallery__slideshow--single-media');
  }

  /**
   * @param {number | string | { id: string }} input
   * @param {Event} [event]
   * @param {{ animate?: boolean }} [options]
   */
  async select(input, event, options = {}) {
    if (!this.#mainSwiper) return;

    const { animate = true } = options;
    const speed = animate ? this.#getTransitionSpeed() : 0;
    let index = this.#resolveIndex(input);

    if (index === undefined || index < 0) return;
    if (index === this.#mainSwiper.activeIndex) return;

    event?.preventDefault();

    const previousIndex = this.#mainSwiper.activeIndex;
    this.#mainSwiper.slideTo(index, speed);
    this.#current = index;
    this.#syncAriaHidden();
    this.#pauseInactiveMedia();

    const slide = this.slides[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * @param {number | string | { id: string }} input
   * @returns {number | undefined}
   */
  #resolveIndex(input) {
    if (typeof input === 'number') return input;

    if (typeof input === 'string') {
      const parsed = parseInt(input, 10);
      return Number.isNaN(parsed) ? undefined : parsed;
    }

    if (input && 'id' in input) {
      const slide = this.slides.find((item) => item.getAttribute('slide-id') == input.id);
      return slide ? this.slides.indexOf(slide) : undefined;
    }

    return undefined;
  }

  /**
   * Register a slide appended at runtime (e.g. AI-generated product image).
   *
   * @param {HTMLElement} slide
   * @param {{ select?: boolean, animate?: boolean }} [options]
   */
  async registerDynamicSlide(slide, options = {}) {
    const { select = true, animate = true } = options;

    if (typeof this.updatedCallback === 'function') {
      this.updatedCallback();
    }

    await new Promise((resolve) => requestAnimationFrame(resolve));

    if (!this.#mainSwiper) {
      this.#initSwipers();
    }

    this.#updateAllSwipers();

    const slideCount = this.slides.length;
    this.#syncMultiSlideMode(slideCount);

    if (select && slide) {
      const index = this.slides.indexOf(slide);
      if (index >= 0) {
        await this.select(index, undefined, { animate });
      }
    }
  }

  /**
   * Append a thumb slide to all thumbs swipers.
   * @param {HTMLElement} thumbSlide
   */
  appendThumbSlide(thumbSlide) {
    const containers = [
      this.refs.thumbsSlidesContainer,
      this.refs.mobileThumbsSlidesContainer,
    ].filter((el) => el instanceof HTMLElement);

    for (const container of containers) {
      container.appendChild(thumbSlide.cloneNode(true));
    }

    this.#updateAllSwipers();
  }

  #updateAllSwipers() {
    this.#thumbsSwiper?.update();
    this.#mobileThumbsSwiper?.update();
    this.#mainSwiper?.update();
    this.#scheduleLoadVisibleThumbs();
  }

  /**
   * Thumbs that slide into the strip window during a programmatic jump can miss
   * their IntersectionObserver callback and stay on the grey placeholder; kick
   * them explicitly once the translate has settled (and again after the animation).
   */
  #scheduleLoadVisibleThumbs() {
    if (this.#loadVisibleFrame) cancelAnimationFrame(this.#loadVisibleFrame);

    this.#loadVisibleFrame = requestAnimationFrame(() => {
      this.#loadVisibleFrame = 0;
      this.#loadVisibleThumbs();
    });

    clearTimeout(this.#loadVisibleTimer);
    this.#loadVisibleTimer = setTimeout(() => this.#loadVisibleThumbs(), SLIDE_TRANSITION_MS + 120);
  }

  #loadVisibleThumbs() {
    for (const thumbsSwiper of [this.#thumbsSwiper, this.#mobileThumbsSwiper]) {
      if (!thumbsSwiper) continue;

      const { first, last } = this.#visibleThumbRange(thumbsSwiper);
      if (first < 0 || last < first) continue;

      for (let index = first; index <= last; index += 1) {
        const slide = thumbsSwiper.slides?.[index];
        if (!(slide instanceof HTMLElement)) continue;

        slide.querySelectorAll('mf-img').forEach((image) => image.loadFunction?.());
      }
    }
  }

  /**
   * Remove dynamically added thumb slides.
   * @param {string} selector
   */
  removeThumbSlides(selector) {
    this.querySelectorAll(selector).forEach((node) => node.remove());
    this.#updateAllSwipers();
  }

  updatedCallback() {
    super.updatedCallback();
  }

  get slides() {
    return this.#getSlideElements();
  }

  get current() {
    return this.#mainSwiper?.activeIndex ?? this.#current;
  }

  /**
   * Opens the zoom dialog at the currently visible slide.
   *
   * @param {PointerEvent} event
   */
  zoomCurrent(event) {
    const gallery = this.closest('media-gallery');
    gallery?.zoom?.(this.current, event);
  }

  set current(value) {
    this.#current = value;
  }

  get swiper() {
    return this.#mainSwiper;
  }

  get thumbsSwiper() {
    return this.#thumbsSwiper;
  }

  /**
   * @param {string | number | null | undefined} variantId
   */
  updateVariantVisibility(variantId) {
    if (this.dataset.hideVariants !== 'true' || variantId == null) return;

    this.#currentVariantId = Number(variantId);

    for (const slide of this.#getShopifySlideElements()) {
      const attached = slide.dataset.attachedToVariant === 'true';
      if (!attached) {
        slide.classList.remove('swiper-slide--hidden');
        continue;
      }

      const variantIds = this.#parseVariantIds(slide.dataset.variantIds);
      const visible = variantIds.includes(Number(variantId));
      slide.classList.toggle('swiper-slide--hidden', !visible);
    }

    this.#updateAllSwipers();
  }

  /**
   * @param {string | number | null | undefined} variantId
   * @param {string | number | null | undefined} featuredMediaId
   */
  handleVariantChange(variantId, featuredMediaId) {
    this.updateVariantVisibility(variantId);

    if (featuredMediaId) {
      this.slideToMediaId(featuredMediaId);
    }
  }

  /**
   * @param {string | number | null | undefined} mediaId
   * @param {boolean} [animate=true]
   */
  slideToMediaId(mediaId, animate = true) {
    if (mediaId == null || mediaId === '') return;

    const id = String(mediaId);
    const slides = this.#getSlideElements();
    const index = slides.findIndex(
      (slide) => slide.dataset.mediaId === id && !slide.classList.contains('swiper-slide--hidden')
    );

    if (index >= 0) {
      this.slideToIndex(index, animate);
    }
  }

  /**
   * @param {number} index
   * @param {boolean} [animate=true]
   */
  slideToIndex(index, animate = true) {
    this.select(index, undefined, { animate });
  }

  /**
   * @param {object} params
   * @param {string} params.url
   * @param {'ai' | 'history'} params.source
   * @param {string} [params.id]
   * @param {string} [params.alt]
   */
  appendDynamicSlide({ url, source, id, alt = '' }, { slideTo = true } = {}) {
    if (!url || !this.#mainSwiper) return null;

    const existingSlide = this.#getSlideElements().find((el) => {
      if (el.dataset.slideSource !== source) return false;
      const img = el.querySelector('img');
      return img?.getAttribute('src') === url;
    });

    if (existingSlide?.dataset.slideId) {
      this.#syncDynamicThumbSlide(url, existingSlide);
      if (slideTo) {
        const index = this.#getSlideElements().indexOf(existingSlide);
        if (index >= 0) {
          this.slideToIndex(index);
        }
      }
      return existingSlide.dataset.slideId;
    }

    this.#removeDynamicSlidesBySource(source);

    const slideId = id || `${source}-${crypto.randomUUID()}`;
    if (this.#getSlideElements().some((el) => el.dataset.slideId === slideId)) return slideId;

    const slideHtml = this.#buildDynamicSlideHtml({ url, source, id: slideId, alt });
    this.#mainSwiper.appendSlide(slideHtml);
    this.#updateAllSwipers();

    const newSlide = this.#getSlideElements().find((el) => el.dataset.slideId === slideId);
    if (newSlide) {
      this.#syncDynamicThumbSlide(url, newSlide);
    }

    if (!this.#dynamicSlides.some((s) => s.id === slideId)) {
      /** @type {DynamicSlideData} */
      const data = { id: slideId, url, source, createdAt: Date.now() };
      this.#dynamicSlides.push(data);
      this.#persistDynamicSlides();
    }

    const newIndex = this.#getSlideElements().length - 1;
    this.#syncMultiSlideMode(this.#getSlideElements().length);

    if (slideTo) {
      this.slideToIndex(newIndex);
    }

    this.dispatchEvent(
      new CustomEvent('media-swiper:dynamic-append', {
        bubbles: true,
        detail: { id: slideId, url, source, index: newIndex },
      })
    );

    return slideId;
  }

  /**
   * @param {string} url
   */
  appendAiSlide(url) {
    return this.appendDynamicSlide({ url, source: 'ai' });
  }

  /**
   * @param {string} url
   * @param {string} [id]
   */
  appendHistorySlide(url, id) {
    return this.appendDynamicSlide({ url, source: 'history', id });
  }

  /**
   * @param {string} id
   */
  removeDynamicSlide(id) {
    const slide = this.#getSlideElements().find((el) => el.dataset.slideId === id);
    if (!slide || !this.#mainSwiper) return;

    const index = this.#getSlideElements().indexOf(slide);
    if (index < 0) return;

    this.#mainSwiper.removeSlide(index);
    this.removeThumbSlides(`[data-dynamic-thumb-for="${id}"]`);
    this.#dynamicSlides = this.#dynamicSlides.filter((s) => s.id !== id);
    this.#persistDynamicSlides();
    this.#updateAllSwipers();
    this.#syncMultiSlideMode(this.#getSlideElements().length);
  }

  /**
   * @param {number} index
   * @returns {HTMLElement | undefined}
   */
  getSlideElement(index) {
    return this.#getSlideElements()[index];
  }

  #applyInitialVariantVisibility() {
    if (this.dataset.hideVariants !== 'true') return;

    const variantId = this.#currentVariantId;
    if (variantId == null) return;

    this.updateVariantVisibility(variantId);
  }

  async #restoreDynamicSlides() {
    const stored = this.#readStoredSlides();
    if (!stored.length) return;

    this.#dynamicSlides = [...stored];

    for (const item of stored) {
      this.appendDynamicSlide(
        { url: item.url, source: item.source, id: item.id },
        { slideTo: false }
      );
    }
  }

  #persistDynamicSlides() {
    const productId = this.dataset.productId;
    if (!productId) return;

    try {
      sessionStorage.setItem(`${STORAGE_PREFIX}${productId}`, JSON.stringify(this.#dynamicSlides));
    } catch {
      // ignore quota errors
    }
  }

  /** @returns {DynamicSlideData[]} */
  #readStoredSlides() {
    const productId = this.dataset.productId;
    if (!productId) return [];

    try {
      const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${productId}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @returns {HTMLElement[]} */
  #getSlideElements() {
    const container = this.refs.slidesContainer;
    if (!(container instanceof HTMLElement)) return [];

    return Array.from(container.querySelectorAll('.swiper-slide'));
  }

  /** @returns {HTMLElement[]} */
  #getShopifySlideElements() {
    return this.#getSlideElements().filter((slide) => slide.dataset.slideSource === 'shopify');
  }

  /**
   * @param {object} params
   * @param {string} params.url
   * @param {'ai' | 'history'} params.source
   * @param {string} params.id
   * @param {string} params.alt
   */
  #buildDynamicSlideHtml({ url, source, id, alt }) {
    const gallery = this.closest('media-gallery');
    const radius = gallery?.style.getPropertyValue('--media-radius') || '0';
    const escapedAlt = alt.replace(/"/g, '&quot;');
    const zoomClass = gallery?.querySelector('zoom-dialog') ? ' product-media-container--zoomable' : '';

    return `
      <div
        class="swiper-slide product-media-container product-media-container--image product-media-container--dynamic product-media-container--${source}${zoomClass}"
        slide-id="${id}"
        data-slide-id="${id}"
        data-slide-source="${source}"
        data-ai-generated="true"
        data-ai-image-url="${url}"
        data-media-id=""
        data-attached-to-variant="false"
        aria-hidden="true"
        style="border-radius: ${radius}px; --media-preview-ratio: 1; --product-media-fit: contain;"
      >
        <div class="product-media-constraint-wrapper">
          <div class="product-media" style="--ratio: 1;">
            <img
              src="${url}"
              alt="${escapedAlt || 'Your Design preview'}"
              class="product-media__image"
              loading="lazy"
              width="1200"
              height="1200"
              style="object-fit: contain; width: 100%; height: 100%;"
            />
          </div>
        </div>
        ${DESIGN_BADGE_MAIN_HTML}
      </div>
    `;
  }

  /**
   * @param {'ai' | 'history'} source
   */
  #removeDynamicSlidesBySource(source) {
    const ids = this.#getSlideElements()
      .filter((el) => el.dataset.slideSource === source)
      .map((el) => el.dataset.slideId)
      .filter(Boolean);

    for (const id of ids) {
      this.removeDynamicSlide(id);
    }

    this.#getSlideElements()
      .filter((el) => el.dataset.slideSource === source)
      .forEach((slide) => {
        const index = this.#getSlideElements().indexOf(slide);
        if (index >= 0) {
          this.#mainSwiper?.removeSlide(index);
        }
      });

    this.removeThumbSlides('[data-ai-generated-control="true"]');
    this.#dynamicSlides = this.#dynamicSlides.filter((s) => s.source !== source);
    this.#persistDynamicSlides();
    this.#updateAllSwipers();
  }

  /**
   * @param {string} url
   * @param {HTMLElement} slide
   */
  #syncDynamicThumbSlide(url, slide) {
    if (this.dataset.hasThumbs !== 'true') return;

    const slideId = slide.dataset.slideId;
    if (!slideId) return;

    this.removeThumbSlides(
      `[data-ai-generated-control="true"], [data-dynamic-thumb-for="${slideId}"]`
    );

    const img = slide.querySelector('img');
    const aspectRatio =
      img?.naturalWidth && img?.naturalHeight
        ? img.naturalWidth / img.naturalHeight
        : 1;

    const thumbSlide = document.createElement('div');
    thumbSlide.className = 'swiper-slide slideshow-controls__thumbnail';
    thumbSlide.setAttribute('data-ai-generated-control', 'true');
    thumbSlide.setAttribute('data-dynamic-thumb-for', slideId);
    thumbSlide.setAttribute('aria-label', 'Your Design preview');
    thumbSlide.style.setProperty('--aspect-ratio', String(aspectRatio));
    thumbSlide.innerHTML = `${buildThumbMfImg(url)}${DESIGN_BADGE_THUMBNAIL_HTML}`;

    this.appendThumbSlide(thumbSlide);
  }

  /**
   * @param {string | undefined} raw
   * @returns {number[]}
   */
  #parseVariantIds(raw) {
    if (!raw) return [];

    return raw
      .split(',')
      .map((v) => parseInt(v.trim(), 10))
      .filter((v) => !Number.isNaN(v));
  }

  #getTransitionSpeed() {
    return prefersReducedMotion() ? 0 : SLIDE_TRANSITION_MS;
  }

  /** @returns {number} */
  #getInitialSlideIndex() {
    const mediaId = this.dataset.initialMediaId;
    if (!mediaId) return 0;

    const slides = this.#getSlideElements();
    const index = slides.findIndex(
      (slide) => slide.dataset.mediaId === String(mediaId) && !slide.classList.contains('swiper-slide--hidden')
    );

    return index >= 0 ? index : 0;
  }

  /**
   * @param {string} name
   * @returns {number | null}
   */
  #parseIntAttr(name) {
    const camel = name.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    const value = this.dataset[camel];
    if (value == null || value === '') return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
}

if (!customElements.get('product-media-swiper')) {
  customElements.define('product-media-swiper', ProductMediaSwiper);
}
